import { arrayIncludes, objectEntries } from '#app/lib/utils'
import { getUriNumericId } from '#app/lib/wikimedia/wikidata'
import { authorRoleProperties } from '#entities/lib/editor/properties_per_subtype'
import { properties, type CustomPropertyConfig } from '#entities/lib/editor/properties_per_type'
import type { EntityType, ExtendedEntityType, InvPropertyClaims, PropertyUri } from '#server/types/entity'

const propertiesWithAllowEntityCreation = [
  ...authorRoleProperties,
  'wdt:P123',
  'wdt:P179',
  'wdt:P195',
  'wdt:P629',
  'wdt:P655',
  'wdt:P737',
  'wdt:P2679',
  'wdt:P2680',
] satisfies PropertyUri[]

interface EditorCustomization {
  datatype?: string
  order?: number
  canValueBeDeleted?: ({ propertyClaims }: { propertyClaims: InvPropertyClaims }) => boolean
  entityValueTypes?: ExtendedEntityType[]
  specialEditActions?: 'author-role'
  allowEntityCreation?: boolean
}

const propertiesEditorsCustomizations: Record<PropertyUri, EditorCustomization> = {
  // instance of
  'wdt:P31': {
    // Further checks, such as preventing type changes, will be performed server-side
    canValueBeDeleted: ({ propertyClaims }) => propertyClaims.length > 1,
  },
  // ISBN-13
  'wdt:P212': {
    datatype: 'fixed-string',
  },
  // main subject
  'wdt:P921': {
    entityValueTypes: [ 'subject' ],
  },
  // ISBN-10
  'wdt:P957': {
    datatype: 'fixed-string',
  },
}

// Sorted by display order
export const prioritizedProperties = [
  'wdt:P31', // instance of
  'wdt:P1476', // title
  'wdt:P1680', // subtitle
  'wdt:P629', // edition of
  'wdt:P212', // ISBN-13
  'wdt:P957', // ISBN-10
  'wdt:P179', // part of serie
  'wdt:P1545', // serie ordinal
  'wdt:P7937', // form of creative work
  'wdt:P136', // genre
  'wdt:P856', // official website
  'wdt:P1581', // official blog URL
  ...authorRoleProperties,
] as PropertyUri[]

for (const property of prioritizedProperties) {
  propertiesEditorsCustomizations[property] ??= {}
  Object.assign(propertiesEditorsCustomizations[property], {
    // The lowest the `order` value, the higher the property will be displayed
    order: prioritizedProperties.indexOf(property) - prioritizedProperties.length,
  })
}

type PropertiesEditorConfig = { property: PropertyUri } & CustomPropertyConfig & EditorCustomization
export const propertiesEditorsConfigs: Record<PropertyUri, PropertiesEditorConfig> = {}

for (const [ property, propertyConfig ] of objectEntries(properties)) {
  const editorCustomization: EditorCustomization = propertiesEditorsCustomizations[property] || {}
  if (arrayIncludes(authorRoleProperties, property)) {
    editorCustomization.specialEditActions = 'author-role'
  }
  if (arrayIncludes(propertiesWithAllowEntityCreation, property)) {
    editorCustomization.allowEntityCreation = true
  }
  editorCustomization.order = editorCustomization.order || (1000 + getUriNumericId(property))
  propertiesEditorsConfigs[property] = Object.assign({ property }, propertyConfig, editorCustomization)
}

const adjustedOrderPerType: Partial<Record<EntityType, Record<PropertyUri, number>>> = {
  edition: {
    'wdt:P110': 100,
  },
}

export function reorderProperties (propertiesUris: PropertyUri[], type: ExtendedEntityType) {
  return propertiesUris.slice().sort((a, b) => {
    if (type in adjustedOrderPerType) {
      const orderA = adjustedOrderPerType[type][a] || propertiesEditorsConfigs[a].order
      const orderB = adjustedOrderPerType[type][b] || propertiesEditorsConfigs[b].order
      return orderA - orderB
    } else {
      return propertiesEditorsConfigs[a].order - propertiesEditorsConfigs[b].order
    }
  })
}
